import { InsightService } from "@pepper/insights-react";
import { CoLoanData, DebtResponse } from "../interfaces/Debt";
import {
  PaymentDataResponse,
  PendingPayment,
  ReusePaymentResponse,
} from "../interfaces/Payment";
import FinancialService from "../services/Financial";
import { StorageService } from "../services/Storage";
import { PaymentMethod, PaymentMethodOrder } from "../interfaces/PaymentMethod";
import GAnalyticsService from "../services/GAnalytics/ganalytics.service";
import dayjs from "dayjs";
import { GTMFlowSteps } from "../interfaces/GTM";
import { ROUTES_PATH } from "../constants";

export const pricesHelper = (price: number): string =>
  price.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const calculateTotalDebtAmount = (debtInfo: DebtResponse): number => {
  return debtInfo.loans.reduce(
    (total, loan) => total + (loan.certified_debt || 0),
    0,
  );
};

export const calculateTotalPromotionsAmount = (
  debtInfo: DebtResponse,
): number => {
  return debtInfo.promotions.reduce(
    (total, promotion) => total + (promotion.amount || 0),
    0,
  );
};

export const calculateTotalExcusedAmount = (debtInfo: DebtResponse): number => {
  return debtInfo.promotions.reduce(
    (total, promotion) => total + (promotion.amount_excused || 0),
    0,
  );
};

export const getDebtAmount = (
  debt: DebtResponse,
  coLoanSelected?: CoLoanData,
  promotions?: boolean,
) => {
  if (debt) {
    let debtAmount = 0;

    if (coLoanSelected && coLoanSelected.certified_debt) {
      debtAmount = coLoanSelected.certified_debt;
    } else if (debt.loans.length > 1 && !promotions) {
      debtAmount = calculateTotalDebtAmount(debt);
    } else if (debt.loans.length >= 1 && !coLoanSelected && promotions) {
      debtAmount = calculateTotalPromotionsAmount(debt);
    } else if (debt.loans.length === 1 && coLoanSelected) {
      debtAmount = coLoanSelected.certified_debt || 0;
    } else if (debt.loans.length === 1) {
      debtAmount = debt.loans[0].certified_debt || 0;
    } else {
      debtAmount = debt.amount || 0;
    }

    return parseFloat(debtAmount.toFixed(2));
  }
};

// Método para obtener todos los ledgers del mayor que siguen sin aplicar
// y poder almacenar el ledgerId
export const getLedger = async (
  documentId: string,
  loanId: string,
  amount: number,
) => {
  try {
    const ledgersFound = await FinancialService.searchLedgers({
      document_id: documentId,
      has_available_amount: true,
    });
    const ledgerInfo = ledgersFound.ledgers.find(
      (ledger) =>
        ledger.loan_id === loanId && ledger.available_amount === amount,
    );
    return ledgerInfo;
  } catch (error) {
    console.error("Error fetching ledger:", error);
    return null;
  }
};

export const newPendingPayment = async (
  response: ReusePaymentResponse | PaymentDataResponse,
) => {
  try {
    let ledgerInfo;
    let cont = 2; // nº de intentos para obtener el ledgerId
    do {
      // Espera un segundo para darle tiempo al backend a que cree el ledger
      await new Promise((resolve) => setTimeout(resolve, 500));
      ledgerInfo = await getLedger(
        response.document_id,
        response.loan_id,
        response.amount,
      );
      if (ledgerInfo && ledgerInfo.ledger_id) {
        const pendingPayment: PendingPayment = {
          loan_id: response.loan_id,
          amount: response.amount,
          expiration_date: new Date(
            new Date().getTime() + 15 * 60 * 1000,
          ).toISOString(),
          ledger_id: ledgerInfo ? ledgerInfo.ledger_id : "",
          user_id: response.document_id ?? "",
        };
        StorageService.setPendingPayment(JSON.stringify(pendingPayment));
        return;
      }
      cont--;
    } while (!ledgerInfo?.ledger_id || cont > 0);
  } catch (error) {
    console.log("Error saving pending payment", error);
  }
};

export const handleBizumSuccessEnd = (
  paymentBizum: PaymentDataResponse,
  initTime: dayjs.Dayjs,
  advanceFee: any,
) => {
  InsightService.logTrace("BizumSuccess", [
    { paymentMethodSelected: PaymentMethod.BIZUM },
    { loanId: paymentBizum?.loan_id },
    { success: true },
  ]);
  if (paymentBizum) {
    GAnalyticsService.sendContinuePayment(
      initTime,
      GTMFlowSteps.TYPES_PAYMENTS,
      {
        payment_method: PaymentMethod.BIZUM,
        payment_transaction_reason: paymentBizum.transaction_type,
        payment_iin_number: "",
        payment_card_brand: "",
        payment_third_used: paymentBizum.third_party,
      },
    );
    GAnalyticsService.sendPurchase({
      currency: "EUR",
      transaction_id: paymentBizum.loan_id,
      value: paymentBizum.amount,
      items: [
        {
          itemId: `payment-${paymentBizum.payment_method_id}`,
          itemName: "Payment-Landing",
        },
      ],
    });
    GAnalyticsService.sendContinuePaymentMethod(initTime);
    // Se guarda el pago realizado en el local storage
    newPendingPayment(paymentBizum);
  }
  if (advanceFee) {
    sessionStorage.setItem("advance-fee-OK", "true");
  }
};

export const savePaymentMethodError = (paymentMethod: PaymentMethod) => {
  const paymentMethodError = sessionStorage.getItem(
    "pepper-payment-method-error",
  );
  if (!paymentMethodError) {
    sessionStorage.setItem("pepper-payment-method-error", paymentMethod);
  }
};

export const removePaymentMethodError = () => {
  sessionStorage.removeItem("pepper-payment-method-error");
};

export const handlePaymentTypesOrder = (
  error: PaymentMethod,
): PaymentMethodOrder => {
  switch (error) {
    case PaymentMethod.EXPRESS:
      return {
        credit_card: 1,
        express: 2,
        apple: 3,
        google: 4,
        bizum: 5,
        bank_account: 6,
        // others: 7,
      };
    case PaymentMethod.CREDIT_CARD:
      return {
        apple: 1,
        google: 2,
        bizum: 3,
        credit_card: 4,
        bank_account: 5,
        // others: 6,
      };
    case PaymentMethod.BIZUM:
      return {
        credit_card: 1,
        apple: 2,
        google: 3,
        bank_account: 4,
        // others: 5,
      };
    case PaymentMethod.APPLE:
    case PaymentMethod.GOOGLE:
      return {
        credit_card: 1,
        bizum: 2,
        bank_account: 3,
        // others: 4,
      };
    case PaymentMethod.BANK_ACCOUNT:
      return {
        express: 1,
        credit_card: 2,
        bizum: 3,
        bank_account: 4,
        others: 5,
      };
    default:
      return {
        express: 1,
        credit_card: 2,
        bizum: 3,
        bank_account: 4,
        apple: 5,
        google: 6,
        others: 7,
      };
  }
};

export const handleErrorPaymentClick = (
  navigate: (path: string) => void,
  initTime: dayjs.Dayjs,
  paymentSelected: unknown,
  flowStep: GTMFlowSteps,
  route: string,
) => {
  GAnalyticsService.sendPaymentError(initTime);
  GAnalyticsService.sendKoEvent(flowStep);
  InsightService.logTrace("Payment Click Error", [
    { route },
    { paymentSelected: JSON.stringify(paymentSelected) },
  ]);
  sessionStorage.setItem("pepper-payment-method-error", PaymentMethod.EXPRESS);
  navigate(ROUTES_PATH.TYPES_PAYMENTS);
};
